/**
 * MobileChartEmbed — full-viewport GEXChartV2 for the mobile app's WebView.
 *
 * Mounted instead of <App/> when the page is opened with ?view=mobile-chart
 * (see index.tsx). No app shell, no AuthContext/login screen: the platform's
 * global fetch wrapper (installApiAuthHeaders) reads the JWT from
 * localStorage['authToken'], which the app injects via
 * injectedJavaScriptBeforeContentLoaded. A postMessage handshake exists as a
 * fallback for setting the token after load.
 *
 * Query params:
 *   symbol   raw alias, e.g. SPX / NDX / RUT / VIX / SPY (default SPX)
 *   interval 1m | 5m | 15m | 1h | 1d (default 5m)
 *
 * Messages posted to the host (window.parent and ReactNativeWebView):
 *   { type: "mmec-chart-ready", symbol }        chart mounted, token present
 *   { type: "mmec-auth-needed" }                no token found at mount
 *   { type: "mmec-auth-ok" }                    reply to a received mmec-auth
 *   { type: "mmec-chart-error", message }       uncaught page error
 * Messages accepted:
 *   { type: "mmec-auth", token }                store JWT and mount the chart
 */
import React, { useEffect, useState } from "react";
import GEXChartV2 from "./components/charts/gex/GEXChartV2";
import { setAuthTokenInMemory } from "./utils/authUtils";

function postToHost(payload: Record<string, unknown>) {
  const msg = JSON.stringify(payload);
  try {
    const rnw = (window as unknown as {
      ReactNativeWebView?: { postMessage: (m: string) => void };
    }).ReactNativeWebView;
    rnw?.postMessage(msg);
  } catch {
    /* not in a RN WebView */
  }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, "*");
    }
  } catch {
    /* no parent */
  }
}

function readStoredToken(): string {
  try {
    return (
      window.localStorage.getItem("authToken") ||
      window.sessionStorage.getItem("authToken") ||
      ""
    );
  } catch {
    return "";
  }
}

export default function MobileChartEmbed() {
  const params = new URLSearchParams(window.location.search);
  const symbol = (params.get("symbol") || "SPX").toUpperCase();
  const interval = params.get("interval") || "5m";

  const [hasToken, setHasToken] = useState<boolean>(() => {
    // getAuthToken() serves only the in-memory token that AuthContext sets at
    // login — it never falls back to storage. The embed has no login flow, so
    // promote the injected token into the in-memory slot itself.
    const tok = readStoredToken();
    if (tok) setAuthTokenInMemory(tok);
    return !!tok;
  });

  // Fallback handshake: accept {type:'mmec-auth', token} after load.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      let data: unknown = e.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      const d = data as { type?: string; token?: string };
      if (d?.type === "mmec-auth" && d.token) {
        try {
          window.localStorage.setItem("authToken", d.token);
        } catch {
          /* storage unavailable */
        }
        setAuthTokenInMemory(d.token);
        setHasToken(true);
        postToHost({ type: "mmec-auth-ok" });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Surface uncaught page errors to the host app.
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      postToHost({
        type: "mmec-chart-error",
        message: String(e.message || "unknown error"),
      });
    };
    window.addEventListener("error", onError);
    return () => window.removeEventListener("error", onError);
  }, []);

  useEffect(() => {
    if (hasToken) {
      postToHost({ type: "mmec-chart-ready", symbol });
    } else {
      postToHost({ type: "mmec-auth-needed" });
    }
  }, [hasToken, symbol]);

  if (!hasToken) {
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--elev, #0f172a)",
          color: "var(--muted, #94a3b8)",
          fontFamily: "system-ui, sans-serif",
          fontSize: 13,
          padding: 24,
          textAlign: "center",
        }}
      >
        Waiting for the app session… (no auth token supplied)
      </div>
    );
  }

  return (
    <div
      style={{
        height: "100dvh",
        width: "100vw",
        overflow: "hidden",
        background: "var(--elev, #0f172a)",
      }}
    >
      <GEXChartV2 initialSymbol={symbol} initialInterval={interval} />
    </div>
  );
}
